import { Button, Icon, Input, Label } from 'semantic-ui-react'
import * as React from 'react'
import { Helmet } from 'react-helmet'
import styled from 'styled-components'
import useSWR from 'swr'
import { z } from 'zod'

import PageHeader from '../../components/pageHeader/pageHeader'
import PageLoader from '../../components/pageLoader'
import { profileSchema, techSchema, Profile, Tech } from '../../types'
import { useTalentApi } from '../../store/mainContext'
import { validateResponse } from '../../utils'
import SimpleMetadataSidePanel from './SimpleMetadataSidePanel'

type MetadataCollection = 'techs' | 'profiles'

type PanelState = {
  open: boolean
  collection: MetadataCollection
  itemId?: string | null
}

const PageContent = styled.div`
  margin: 0px 50px 50px 50px;
`

const sortByText = <T extends { text: string }>(items: T[]) =>
  [...items].sort((a, b) => a.text.localeCompare(b.text))

const collectionConfig = {
  techs: {
    title: 'Techs',
    singularTitle: 'Tech',
    description: 'Used in candidate main and secondary tech selections.',
    emptyText: 'No techs configured yet.',
    addLabel: 'Add Tech',
  },
  profiles: {
    title: 'Profiles',
    singularTitle: 'Profile',
    description:
      'Used in candidate profile selections and position profile requirements.',
    emptyText: 'No profiles configured yet.',
    addLabel: 'Add Profile',
  },
} as const

const EmptyState = ({
  title,
  description,
  actionLabel,
  onAction,
}: {
  title: string
  description: string
  actionLabel: string
  onAction(): void
}) => (
  <div className="flex min-h-[280px] flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-white px-8 text-center">
    <div className="mb-3 text-xl font-semibold text-slate-800">{title}</div>
    <p className="mb-6 max-w-lg text-base leading-relaxed text-slate-500">
      {description}
    </p>
    <Button primary onClick={onAction}>
      <Icon name="add" /> {actionLabel}
    </Button>
  </div>
)

export default function MetadataSettingsPage() {
  const api = useTalentApi()

  const techs = useSWR('settings-techs', () =>
    api
      .get('techs')
      .then(validateResponse(z.array(techSchema)))
      .then(sortByText),
  )

  const profiles = useSWR('settings-profiles', () =>
    api
      .get('profiles')
      .then(validateResponse(z.array(profileSchema)))
      .then(sortByText),
  )

  const [panelState, setPanelState] = React.useState<PanelState>({
    open: false,
    collection: 'techs',
    itemId: null,
  })
  const [activeCollection, setActiveCollection] =
    React.useState<MetadataCollection>('techs')
  const [search, setSearch] = React.useState('')
  const deferredSearch = React.useDeferredValue(search)

  const isLoading = techs.isLoading || profiles.isLoading

  const openCreatePanel = (collection: MetadataCollection) => {
    setPanelState({ open: true, collection, itemId: null })
  }

  const openEditPanel = (
    collection: MetadataCollection,
    item: Tech | Profile,
  ) => {
    setPanelState({ open: true, collection, itemId: item._id })
  }

  const closePanel = () => {
    setPanelState((prev) => ({ ...prev, open: false, itemId: null }))
  }

  const refreshCollection = async (collection: MetadataCollection) => {
    if (collection === 'techs') {
      await techs.mutate()
      return
    }
    await profiles.mutate()
  }

  const currentConfig = collectionConfig[activeCollection]
  const currentTitle = collectionConfig[panelState.collection].singularTitle
  const activeItems =
    activeCollection === 'techs' ? techs.data || [] : profiles.data || []
  const normalizedSearch = deferredSearch.trim().toLowerCase()
  const filteredItems = normalizedSearch
    ? activeItems.filter((item) =>
        item.text.toLowerCase().includes(normalizedSearch),
      )
    : activeItems
  const featuredItems = activeItems.slice(0, 3)

  if (isLoading) return <PageLoader />

  return (
    <React.Fragment>
      <Helmet>
        <title>Settings &gt; Metadata</title>
      </Helmet>

      <PageHeader
        breadcrumb={[
          { text: 'Dashboard', link: '/' },
          { text: 'Settings', link: '/settings' },
          { text: 'Metadata', link: '/settings/metadata' },
        ]}
        title="Metadata"
        sub="Manage candidate metadata from a single workspace instead of jumping between separate tables."
      />

      <PageContent>
        <div className="grid gap-6 lg:grid-cols-[320px_minmax(0,1fr)]">
          <aside className="space-y-4 self-start lg:sticky lg:top-24">
            {(Object.keys(collectionConfig) as MetadataCollection[]).map(
              (collection) => {
                const config = collectionConfig[collection]
                const items =
                  collection === 'techs'
                    ? techs.data || []
                    : profiles.data || []
                const isActive = collection === activeCollection

                return (
                  <button
                    key={collection}
                    type="button"
                    onClick={() => {
                      setActiveCollection(collection)
                      setSearch('')
                    }}
                    className={[
                      'w-full rounded-2xl border p-5 text-left transition',
                      isActive
                        ? 'border-slate-900 bg-slate-900 text-white shadow-lg'
                        : 'border-slate-200 bg-white text-slate-900 hover:border-slate-400 hover:shadow-sm',
                    ].join(' ')}
                  >
                    <div className="mb-3 flex items-center justify-between">
                      <div className="text-xl font-semibold">
                        {config.title}
                      </div>
                      <Label
                        circular
                        className={isActive ? '!bg-white !text-slate-900' : ''}
                      >
                        {items.length}
                      </Label>
                    </div>
                    <p
                      className={[
                        'mb-4 text-sm leading-relaxed',
                        isActive ? 'text-slate-200' : 'text-slate-500',
                      ].join(' ')}
                    >
                      {config.description}
                    </p>
                    {items.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {items.slice(0, 3).map((item) => (
                          <span
                            key={item._id}
                            className={[
                              'rounded-full px-3 py-1 text-sm',
                              isActive
                                ? 'bg-white/15 text-white'
                                : 'bg-slate-100 text-slate-700',
                            ].join(' ')}
                          >
                            {item.text}
                          </span>
                        ))}
                      </div>
                    )}
                  </button>
                )
              },
            )}
          </aside>

          <section className="rounded-3xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 px-6 py-5">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                <div>
                  <div className="mb-1 flex items-center gap-3">
                    <h2 className="mb-0">{currentConfig.title}</h2>
                    <Label className="!ml-0">{activeItems.length} total</Label>
                  </div>
                  <p className="mb-0 max-w-2xl text-slate-500">
                    {currentConfig.description}
                  </p>
                </div>

                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                  <Input
                    icon="search"
                    placeholder={`Search ${currentConfig.title.toLowerCase()}...`}
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    className="min-w-[280px]"
                  />

                  <Button
                    primary
                    onClick={() => openCreatePanel(activeCollection)}
                  >
                    <Icon name="add" /> {currentConfig.addLabel}
                  </Button>
                </div>
              </div>

              {featuredItems.length > 0 && !normalizedSearch && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {featuredItems.map((item) => (
                    <button
                      key={item._id}
                      type="button"
                      onClick={() => openEditPanel(activeCollection, item)}
                      className="rounded-full bg-slate-100 px-3 py-1.5 text-sm text-slate-700 transition hover:bg-slate-200"
                    >
                      {item.text}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="px-3 py-3">
              {activeItems.length === 0 ? (
                <EmptyState
                  title={`No ${currentConfig.title.toLowerCase()} yet`}
                  description={`Create the first ${currentConfig.singularTitle.toLowerCase()} to make it available in candidate forms and filters.`}
                  actionLabel={currentConfig.addLabel}
                  onAction={() => openCreatePanel(activeCollection)}
                />
              ) : filteredItems.length === 0 ? (
                <EmptyState
                  title="No matches"
                  description={`No ${currentConfig.title.toLowerCase()} match “${deferredSearch}”. Try a broader search or add a new one.`}
                  actionLabel={currentConfig.addLabel}
                  onAction={() => openCreatePanel(activeCollection)}
                />
              ) : (
                <div className="space-y-2">
                  {filteredItems.map((item, index) => (
                    <button
                      key={item._id}
                      type="button"
                      onClick={() => openEditPanel(activeCollection, item)}
                      className="flex w-full items-center justify-between rounded-2xl border border-transparent px-4 py-4 text-left transition hover:border-slate-200 hover:bg-slate-50"
                    >
                      <div className="min-w-0">
                        <div className="mb-1 text-lg font-medium text-slate-900">
                          {item.text}
                        </div>
                        <div className="text-sm text-slate-500">
                          {currentConfig.singularTitle} #{index + 1}
                        </div>
                      </div>

                      <div className="flex items-center gap-3 text-slate-400">
                        {normalizedSearch && (
                          <Label basic size="small">
                            Match
                          </Label>
                        )}
                        <Icon name="angle right" />
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </section>
        </div>

        <SimpleMetadataSidePanel
          open={panelState.open}
          collection={panelState.collection}
          itemId={panelState.itemId ?? undefined}
          title={currentTitle}
          onClose={closePanel}
          onSaved={() =>
            refreshCollection(panelState.collection).then(() => closePanel())
          }
          onDeleted={() =>
            refreshCollection(panelState.collection).then(() => closePanel())
          }
        />
      </PageContent>
    </React.Fragment>
  )
}
